import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpenText,
  Check,
  Play,
  Download,
  Star,
  Users,
  Clock,
  Certificate,
  ArrowRight,
  ShieldCheck,
  GraduationCap,
  ChatCircleDots,
  Sparkle,
  Quotes,
  CaretDown,
  Heart,
  Books,
  UsersThree,
  HandHeart,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUDIENCE = [
  { icon: BookOpenText, title: "K-5 Classroom Teachers", desc: "Zero-prep Tier-2 & Tier-3 vocabulary that fits any curriculum." },
  { icon: Heart, title: "SPED Specialists", desc: "Neurodiversity-affirming scaffolds for every reading level." },
  { icon: Users, title: "ESL / ELL Instructors", desc: "Contextual comprehension for multilingual learners." },
  { icon: ChatCircleDots, title: "Speech-Language Pathologists", desc: "Language-support strategies backed by evidence." },
  { icon: HandHeart, title: "Occupational Therapists", desc: "Fine-motor + vocabulary in one traceable page." },
  { icon: Books, title: "Homeschool Parents", desc: "A ready-to-teach system you can start today." },
];

const CURRICULUM = [
  { id: "m1", title: "Module 1 — Foundational Tier-2 & Tier-3 Vocabulary", detail: "Word-selection frameworks, robust review cycles, and the words that unlock every subject." },
  { id: "m2", title: "Module 2 — Multisensory Word Learning", detail: "Trace-say-write-move routines that build stronger memory in less time." },
  { id: "m3", title: "Module 3 — SPED & Neurodiversity Scaffolds", detail: "Chunked instruction, sensory-friendly pacing, and modification templates for IEP goals." },
  { id: "m4", title: "Module 4 — ESL/ELL Contextual Comprehension", detail: "Bridge L1 → L2 with visuals, cognates, and structured context practice." },
  { id: "m5", title: "Module 5 — Interactive Games & Sentence Builders", detail: "Turn any lesson into a game — including AI-tutor sessions kids can do independently." },
  { id: "m6", title: "Module 6 — Assessment & Progress Tracking", detail: "Quick screeners, progress rubrics, and printable completion certificates for your class." },
];

const FAQS = [
  { q: "Can I use school-district or PD funds?", a: "Yes — you'll receive an itemized receipt at checkout that most districts accept for reimbursement or PD credit." },
  { q: "Is this suitable for SPED or ESL students?", a: "Absolutely. Every module includes concrete modifications, IEP-friendly scaffolds, and multilingual comprehension strategies." },
  { q: "Do I get instant access to the printables?", a: "Yes. After checkout you receive an email with instant lifetime access to every video lesson, printable PDF, the AI Tutor, and the completion certificate." },
  { q: "What if it's not the right fit?", a: "30-day money-back guarantee, no questions asked. Just email us and we'll refund the full $97." },
  { q: "How is this different from a free YouTube video?", a: "This is a complete system: sequenced training, 50+ classroom-ready printables, an AI tutor for your students, and a certificate you can log for PD hours." },
];

const CoursePage = () => {
  const [courseInfo, setCourseInfo] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [stripeMode, setStripeMode] = useState("test");

  useEffect(() => {
    axios.get(`${API}/course/info`).then((r) => setCourseInfo(r.data)).catch(() => {});
    axios.get(`${API}/stripe/mode`).then((r) => setStripeMode(r.data.mode)).catch(() => {});
  }, []);

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!email || !name) {
      toast.error("Please enter your name and email");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/course/checkout`, {
        email,
        name,
        origin_url: window.location.origin,
      });
      window.location.href = response.data.checkout_url;
    } catch {
      toast.error("Failed to start checkout. Please try again.");
      setIsLoading(false);
    }
  };

  const scrollToCheckout = () => {
    document.getElementById("checkout")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-background">
      {stripeMode !== "live" && (
        <div className="bg-yellow-100 border-b-2 border-yellow-400 text-foreground font-nunito text-sm py-2 px-4 text-center" data-testid="stripe-test-banner">
          ⚠️ <strong>Test Mode</strong> — payments are simulated. Use card <code className="bg-white px-1 rounded">4242 4242 4242 4242</code> with any future date.
        </div>
      )}

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-background/85 backdrop-blur-md border-b-2 border-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center gap-2">
              <BookOpenText size={30} weight="duotone" className="text-primary" />
              <span className="font-fredoka font-bold text-lg sm:text-xl text-foreground">
                Embrace the Magic of Words
              </span>
            </a>
            <div className="hidden md:flex items-center gap-4">
              <a href="#curriculum" className="font-nunito font-semibold text-sm text-slate-700 hover:text-foreground">Curriculum</a>
              <a href="#story" className="font-nunito font-semibold text-sm text-slate-700 hover:text-foreground">Instructor</a>
              <a href="#faq" className="font-nunito font-semibold text-sm text-slate-700 hover:text-foreground">FAQ</a>
              <Button
                onClick={scrollToCheckout}
                className="bg-primary hover:bg-primary-hover text-white font-nunito font-bold border-2 border-foreground shadow-brutal-sm active-brutal rounded-full"
                data-testid="nav-enroll-btn"
              >
                Enroll — $97
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden py-14 lg:py-20 px-4">
        <div className="absolute inset-0 bg-[radial-gradient(#CBD5E1_1px,transparent_1px)] [background-size:20px_20px] opacity-30 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 bg-white border-2 border-foreground rounded-full px-3 py-1.5 shadow-brutal-sm mb-5">
                <GraduationCap size={16} weight="fill" className="text-primary" />
                <span className="font-nunito font-bold text-xs text-foreground uppercase tracking-wider">
                  Comprehensive K-5 Professional Development
                </span>
              </span>

              <h1 className="font-fredoka font-bold text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05] mb-5">
                Vocabulary & Language Support for K-5 Educators & Specialists
              </h1>
              <p className="font-nunito text-lg sm:text-xl text-slate-700 mb-8 max-w-xl leading-relaxed">
                A step-by-step framework to unlock word learning for general ed, SPED, ESL/ELL, OT & homeschool students —
                with 50+ printables, an AI Tutor for your kids, and a certificate for your PD file.
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                <Button
                  onClick={scrollToCheckout}
                  className="bg-primary hover:bg-primary-hover text-white font-nunito font-bold text-base border-2 border-foreground shadow-brutal active-brutal rounded-full px-7 py-5"
                  data-testid="hero-enroll-btn"
                >
                  Enroll for $97 — Instant Access
                  <ArrowRight size={20} weight="bold" className="ml-2" />
                </Button>
                <a href="#curriculum" data-testid="hero-syllabus-btn">
                  <Button variant="outline" className="bg-white text-foreground font-nunito font-bold text-base border-2 border-foreground shadow-brutal-sm rounded-full px-7 py-5">
                    View Curriculum
                  </Button>
                </a>
              </div>

              <div className="flex flex-wrap gap-5 items-center text-sm">
                <div className="flex items-center gap-2 text-slate-700">
                  <ShieldCheck size={20} weight="fill" className="text-emerald-600" />
                  <span className="font-nunito font-bold">30-day money back</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <Clock size={20} weight="fill" className="text-primary" />
                  <span className="font-nunito font-bold">Lifetime access</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <Certificate size={20} weight="fill" className="text-accent-purple" />
                  <span className="font-nunito font-bold">PD certificate</span>
                </div>
              </div>
            </motion.div>

            {/* Right: preview card with amber corner */}
            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative bg-white border-2 border-foreground rounded-3xl shadow-soft-editorial p-6 sm:p-8">
                <div className="absolute -top-3 -right-3 rotate-6 z-10">
                  <span className="inline-block bg-secondary text-foreground font-nunito font-black text-xs uppercase tracking-widest px-3 py-1.5 border-2 border-foreground shadow-brutal-sm rounded-md">
                    Save $143
                  </span>
                </div>
                <p className="font-nunito text-xs text-slate-500 uppercase tracking-widest mb-1">One-time payment</p>
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="font-fredoka font-bold text-6xl text-primary leading-none">$97</span>
                  <span className="font-nunito text-xl text-slate-400 line-through">$240</span>
                </div>
                <p className="font-nunito text-sm text-slate-600 mb-5">
                  <strong>$240 value</strong> · Lifetime access · Instant download
                </p>
                <ul className="space-y-2.5 mb-6">
                  {["6 training modules", "50+ printable templates", "AI Tutor + Kid's Corner", "Completion Certificate", "SPED, ESL & OT scaffolds"].map((f) => (
                    <li key={f} className="flex items-start gap-2 font-nunito text-sm text-slate-700">
                      <span className="mt-0.5 w-4 h-4 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
                        <Check size={10} weight="bold" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={scrollToCheckout}
                  className="w-full bg-primary hover:bg-primary-hover text-white font-nunito font-bold text-base border-2 border-foreground shadow-brutal active-brutal rounded-xl py-5"
                  data-testid="preview-enroll-btn"
                >
                  Enroll Now
                  <ArrowRight size={20} weight="bold" className="ml-2" />
                </Button>
                <div className="mt-4 flex items-center justify-center gap-2 text-slate-500">
                  <ShieldCheck size={16} weight="fill" />
                  <span className="font-nunito text-xs">Secure Stripe checkout</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-16 sm:py-20 px-4 bg-white border-y-2 border-foreground">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <span className="font-caveat text-2xl text-accent-pink rotate-2 inline-block">
              Built for every kind of educator
            </span>
            <h2 className="font-fredoka font-bold text-3xl sm:text-4xl text-foreground mt-2">
              Who this course is for
            </h2>
            <p className="font-nunito text-slate-600 mt-3 max-w-2xl mx-auto">
              Whether you're supporting one child at home or 27 in a classroom, this system meets you where you are.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="target-audience-grid">
            {AUDIENCE.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-background border-2 border-foreground rounded-2xl p-5 shadow-brutal-sm hover:shadow-brutal transition-shadow"
              >
                <div className="w-11 h-11 bg-sky-100 border-2 border-foreground rounded-xl flex items-center justify-center mb-3">
                  <p.icon size={24} weight="duotone" className="text-primary" />
                </div>
                <h3 className="font-fredoka font-bold text-lg text-foreground mb-1">{p.title}</h3>
                <p className="font-nunito text-sm text-slate-600 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section id="curriculum" className="py-16 sm:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="font-caveat text-2xl text-accent-purple -rotate-2 inline-block">
              The full curriculum
            </span>
            <h2 className="font-fredoka font-bold text-3xl sm:text-4xl text-foreground mt-2">
              6 modules · 50+ printables
            </h2>
            <p className="font-nunito text-slate-600 mt-3">
              Tap any module to see what's inside.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-3" data-testid="curriculum-accordion">
            {CURRICULUM.map((m) => (
              <AccordionItem
                key={m.id}
                value={m.id}
                className="bg-white border-2 border-foreground rounded-2xl shadow-soft-editorial px-5 sm:px-6"
              >
                <AccordionTrigger className="font-fredoka font-bold text-base sm:text-lg text-foreground py-5 hover:no-underline">
                  {m.title}
                </AccordionTrigger>
                <AccordionContent className="font-nunito text-slate-700 pb-5 leading-relaxed">
                  {m.detail}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Instructor Story */}
      <section id="story" className="py-16 sm:py-20 px-4 bg-amber-50 border-y-2 border-foreground">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-2">
              <div className="relative bg-white border-2 border-foreground rounded-3xl shadow-brutal p-4 rotate-[-2deg] max-w-xs mx-auto">
                <div className="aspect-[4/5] bg-gradient-to-br from-sky-100 to-purple-100 rounded-2xl border-2 border-foreground flex items-center justify-center">
                  <GraduationCap size={100} weight="duotone" className="text-primary/70" />
                </div>
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 rotate-[3deg]">
                  <span className="inline-block bg-secondary text-foreground font-caveat text-2xl px-4 py-1 border-2 border-foreground shadow-brutal-sm rounded-md whitespace-nowrap">
                    — Ann, a mom teaching her daughter at home
                  </span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-3">
              <span className="font-caveat text-2xl text-accent-pink rotate-2 inline-block">
                Meet your instructor
              </span>
              <h2 className="font-fredoka font-bold text-3xl sm:text-4xl text-foreground mt-2 mb-5">
                Hi — I'm Ann.
              </h2>
              <p className="font-nunito text-slate-700 text-base sm:text-lg leading-relaxed mb-4">
                I'm a mom — not a big publisher. Every workbook, every module, every AI helper in this course
                started as something I made to teach my own special-needs daughter at home.
              </p>
              <p className="font-nunito text-slate-700 text-base sm:text-lg leading-relaxed mb-4">
                What worked for her — the tracing, the gentle repetition, the SPED and ESL scaffolds, the
                sensory pacing — is exactly what's inside this course. If it helped her light up around words,
                I know it can help the children in your care too.
              </p>
              <p className="font-nunito text-slate-700 text-base sm:text-lg leading-relaxed">
                My hope: that after this course, no child in your care leaves the year with fewer words than they deserve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Checkout / Pricing */}
      <section id="checkout" className="py-16 sm:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <span className="font-caveat text-2xl text-accent-pink -rotate-2 inline-block">
                Everything included
              </span>
              <h2 className="font-fredoka font-bold text-3xl sm:text-4xl text-foreground mt-2 mb-6">
                What you get for $97
              </h2>
              <ul className="space-y-3">
                {[
                  ["6 in-depth training modules", "Video + written + printable per module"],
                  ["50+ ready-to-use printables", "PDFs with answer keys included"],
                  ["AI Tutor for your students", "1-on-1 vocabulary help powered by GPT-5.2"],
                  ["Interactive Kid's Corner", "Word Game, Sentence Builder, Story Creator"],
                  ["Completion Certificate", "Printable, personalized, PD-ready"],
                  ["Lifetime access + updates", "Buy once, keep forever"],
                ].map(([title, sub]) => (
                  <li key={title} className="flex gap-3">
                    <span className="mt-1 w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
                      <Check size={14} weight="bold" />
                    </span>
                    <div>
                      <p className="font-fredoka font-bold text-foreground">{title}</p>
                      <p className="font-nunito text-sm text-slate-600">{sub}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Checkout card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white border-2 border-foreground rounded-3xl shadow-soft-editorial p-6 sm:p-8"
              data-testid="course-pricing-card"
            >
              <div className="text-center mb-6">
                <p className="font-nunito text-xs text-slate-500 uppercase tracking-widest mb-1">
                  One-time payment · Lifetime access
                </p>
                <div className="flex items-baseline justify-center gap-2 mb-1">
                  <span className="font-fredoka font-bold text-6xl text-primary leading-none">$97</span>
                  <span className="font-nunito text-xl text-slate-400 line-through">$240</span>
                </div>
                <span className="inline-block bg-emerald-100 text-emerald-800 font-nunito font-bold text-xs uppercase tracking-wider px-3 py-1 rounded-full">
                  🎁 $143 off — limited time
                </span>
              </div>

              <form onSubmit={handleCheckout} className="space-y-3">
                <div>
                  <label className="font-nunito font-bold text-sm text-foreground mb-1.5 block">
                    Your name
                  </label>
                  <Input
                    type="text"
                    placeholder="Ann Rodriguez"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="font-nunito border-2 border-foreground rounded-xl py-5"
                    data-testid="course-name-input"
                  />
                </div>
                <div>
                  <label className="font-nunito font-bold text-sm text-foreground mb-1.5 block">
                    Email address
                  </label>
                  <Input
                    type="email"
                    placeholder="you@school.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="font-nunito border-2 border-foreground rounded-xl py-5"
                    data-testid="course-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-primary hover:bg-primary-hover text-white font-nunito font-bold text-base border-2 border-foreground shadow-brutal active-brutal rounded-xl py-6 mt-2"
                  data-testid="course-checkout-btn"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      Processing…
                    </span>
                  ) : (
                    <>
                      Get Instant Access — $97
                      <ArrowRight size={20} weight="bold" className="ml-2" />
                    </>
                  )}
                </Button>
              </form>

              {/* Trust badges */}
              <div className="mt-5 pt-5 border-t border-slate-100 space-y-2">
                <div className="flex items-center gap-2 text-slate-600">
                  <ShieldCheck size={18} weight="fill" className="text-emerald-600" />
                  <span className="font-nunito text-sm"><strong>30-day</strong> money-back guarantee</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <ShieldCheck size={18} weight="fill" />
                  <span className="font-nunito text-sm">Secure checkout via Stripe</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Clock size={18} weight="fill" className="text-primary" />
                  <span className="font-nunito text-sm">Instant access after payment</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 sm:py-20 px-4 bg-white border-t-2 border-foreground">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="font-caveat text-2xl text-accent-purple -rotate-2 inline-block">
              We've got answers
            </span>
            <h2 className="font-fredoka font-bold text-3xl sm:text-4xl text-foreground mt-2">
              Frequently asked questions
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="course-faq">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-background border-2 border-foreground rounded-2xl shadow-brutal-sm px-5"
              >
                <AccordionTrigger className="font-fredoka font-bold text-base sm:text-lg text-foreground py-4 hover:no-underline text-left">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="font-nunito text-slate-700 pb-5 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-10">
            <Button
              onClick={scrollToCheckout}
              className="bg-primary hover:bg-primary-hover text-white font-nunito font-bold text-lg border-2 border-foreground shadow-brutal active-brutal rounded-full px-8 py-6"
              data-testid="faq-enroll-btn"
            >
              Enroll for $97 — Instant Access
              <ArrowRight size={20} weight="bold" className="ml-2" />
            </Button>
            <p className="font-nunito text-sm text-slate-500 mt-3">
              30-day money-back guarantee · Secure Stripe checkout
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-white py-10 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-fredoka font-bold text-lg mb-2">Embrace the Magic of Words</p>
          <p className="font-nunito text-sm text-white/60 mb-4">
            Made by a mom teaching her special-needs daughter at home.
          </p>
          <p className="font-nunito text-xs text-white/40">
            © 2026 Embrace the Magic of Words. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CoursePage;
